import {
  ButtonGroup,
  DataStatus,
  Form,
  FormButton,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Heading, Text, VStack} from 'platform/foundation';

import {isFalse, isNilOrEmpty, isNonEmptyString} from 'ramda-adjunct';

import {
  useGetServiceOrderIntegrationsMazdaAdditionalInformationQuery,
  usePatchServiceOrderIntegrationsMazdaAdditionalInformationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {
  EMPTY_DTC_CODE,
  mazdaAdditionalInformationFormSchema,
  MazdaAdditionalInformationFormType,
} from '../types/mazdaAdditionalInformationFormSchema';
import {MazdaInformationFormFields} from './MazdaInformationFormFields';

interface OrderMazdaInformationProps extends TestIdProps {
  orderId: string;
  serviceCaseId: string;
}

export function OrderMazdaInformation(props: OrderMazdaInformationProps) {
  const {
    data: mazdaAdditionalInformation,
    isError: isMazdaAdditionalInformationError,
    isLoading: isMazdaAdditionalInformationLoading,
  } = useGetServiceOrderIntegrationsMazdaAdditionalInformationQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
    },
    {refetchOnMountOrArgChange: true}
  );
  const [patchMazdaAdditionalInformation] =
    usePatchServiceOrderIntegrationsMazdaAdditionalInformationMutation();

  const isReadOnly = isFalse(mazdaAdditionalInformation?.isEditable);

  const handleSubmit: FormSubmitHandler<MazdaAdditionalInformationFormType> = (data) =>
    patchMazdaAdditionalInformation({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      body: {
        assemblyGroup: data.assemblyGroup ?? null,
        subAssemblyGroup: data.subAssemblyGroup ?? null,
        pncCode: data.pncCode ?? null,
        symptomCode: data.symptomCode ?? null,
        damageCode: data.damageCode ?? null,
        locationCodes: data.locationCodes ?? [],
        dtcCodes: data.dtcCodes.map((dtcCode) => dtcCode.value.trim()).filter(isNonEmptyString),
      },
    })
      .unwrap()
      .catch(handleApiError);

  const defaultValues: MazdaAdditionalInformationFormType = {
    assemblyGroup: mazdaAdditionalInformation?.assemblyGroup ?? undefined,
    subAssemblyGroup: mazdaAdditionalInformation?.subAssemblyGroup ?? undefined,
    pncCode: mazdaAdditionalInformation?.pncCode ?? undefined,
    symptomCode: mazdaAdditionalInformation?.symptomCode ?? undefined,
    damageCode: mazdaAdditionalInformation?.damageCode ?? undefined,
    locationCodes: mazdaAdditionalInformation?.locationCodes ?? undefined,
    dtcCodes: isNilOrEmpty(mazdaAdditionalInformation?.dtcCodes)
      ? [EMPTY_DTC_CODE]
      : (mazdaAdditionalInformation?.dtcCodes ?? []).map((dtcCode) => ({value: dtcCode})),
  };

  return (
    <DataStatus
      isLoading={isMazdaAdditionalInformationLoading}
      isError={isMazdaAdditionalInformationError}
    >
      <VStack spacing={1}>
        <Heading size={4}>{i18n.t('entity.serviceOrder.labels.mazdaPortalIntegration')}</Heading>
        <Text size="xSmall" color="secondary">
          {i18n.t('entity.serviceOrder.labels.mazdaPortalIntegrationDescription')}
        </Text>
      </VStack>
      <Separator />
      <Form<MazdaAdditionalInformationFormType>
        onSubmit={handleSubmit}
        experimentalZodSchema={mazdaAdditionalInformationFormSchema}
        defaultValues={defaultValues}
      >
        {(control, formApi) => (
          <VStack spacing={4}>
            <MazdaInformationFormFields
              control={control}
              formApi={formApi}
              isReadOnly={isReadOnly}
              data-testid={props['data-testid']}
            />
            <ButtonGroup align="right">
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.saveChanges')}
                isDisabled={isReadOnly}
                data-testid={suffixTestId('save', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
